// src/lib/resources/types/tools/quiz.ts

/**
 * @fileoverview Core quiz types and interfaces for implementing interactive quizzes.
 * Provides comprehensive type definitions for quiz structure, questions, scoring and feedback.
 * 
 * @example
 * ```typescript
 * const quiz: Quiz = {
 *   resourceType: 'tool',
 *   toolType: 'quiz',
 *   sections: [{
 *     id: 'section1',
 *     title: 'Basic Questions',
 *     objectives: ['Test knowledge of X'],
 *     questions: [...]
 *   }],
 *   config: {
 *     passingScore: 80,
 *     showAnswersImmediately: true
 *   }
 * }
 * ```
 */

import type { BaseTool, SupplementalResourceReference } from '~/lib/resources/types/shared/base'
import type { DifficultyLevel, LearningResourceType } from '~/lib/resources/types/shared/enums'
import type { BaseStep } from '~/lib/resources/types/shared/sections'
import type { BaseToolConfig } from '~/lib/resources/types/shared/configs'
import type { QuestionInput } from '~/lib/resources/types/shared/inputs'

// ==============================
// Core Question Types
// ==============================

/**
 * Available question formats that can be used in a quiz.
 * 
 * TEXT: Free text input for open-ended responses
 * NUMBER: Numeric input for mathematical/quantitative questions  
 * SELECT: Single choice dropdown for choosing one option
 * MULTISELECT: Multiple choice dropdown allowing multiple selections
 * RADIO: Single choice radio buttons for mutually exclusive options
 * CHECKBOX: Multiple choice checkboxes for selecting multiple answers
 * ORDERED: Sequence ordering question for arranging items
 * VISUAL: Image-based options for visual assessments
 */
export const QuestionTypes = {
  TEXT: 'text',
  NUMBER: 'number', 
  SELECT: 'select',
  MULTISELECT: 'multiselect',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  ORDERED: 'ordered',
  VISUAL: 'visual'
} as const

export type QuestionType = typeof QuestionTypes[keyof typeof QuestionTypes]

/**
 * Platform-specific content targeting to customize quiz content per platform.
 * Enables tailored questions and content for different social media contexts.
 * 
 * META: Content specific to Meta platforms (Facebook, Instagram)
 * TIKTOK: Content optimized for TikTok format
 * YOUTUBE: YouTube-specific content and questions
 * GOOGLE: Google Ads and Analytics specific content
 * GENERAL: Platform-agnostic content suitable for any context
 */
export const Platforms = {
  META: 'meta',
  TIKTOK: 'tiktok',
  YOUTUBE: 'youtube',
  GOOGLE: 'google',
  GENERAL: 'general'
} as const

export type Platform = typeof Platforms[keyof typeof Platforms]

/**
 * Value display formats for metrics and scores.
 * Controls how numeric values are formatted in the UI.
 * 
 * NUMBER: Plain number format (e.g., "42")
 * CURRENCY: Monetary value (e.g., "$42.00")
 * PERCENTAGE: Percentage format (e.g., "42%")
 * RATIO: Ratio format (e.g., "42/100")
 */
export const MetricFormats = {
  NUMBER: 'number',
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
  RATIO: 'ratio'
} as const

export type MetricFormat = typeof MetricFormats[keyof typeof MetricFormats]

/**
 * Threshold types for metric evaluation and feedback.
 * Used to categorize performance levels and trigger appropriate responses.
 * 
 * SUCCESS: Indicates achievement of target performance
 * WARNING: Indicates borderline or concerning performance
 * ERROR: Indicates performance below acceptable threshold
 * INFO: Indicates neutral or informational status
 */
export const ThresholdTypes = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info'
} as const

export type ThresholdType = typeof ThresholdTypes[keyof typeof ThresholdTypes]

// ==============================
// Quiz Structure Interfaces
// ==============================

/**
 * Quiz section interface extending BaseStep
 * Represents a logical grouping of related quiz questions
 */
export interface QuizSection extends BaseStep {
  /** Learning goals for this section */
  objectives: string[]
  /** Questions in this section */
  questions: QuizQuestion[]
  /** Optional platform-specific question */
  platform?: Platform
  /** Section completion feedback */
  feedback?: string
  /** Learning content for this section */
  learningContent?: {
    /** Title of the learning content section */
    title: string
    /** Description of what users will learn */
    description: string
    /** Array of related learning resources */
    resources: SupplementalResourceReference[]
  }
  /** Feedback threshold percentage (0-100) */
  feedbackThreshold?: number
}

/**
 * Quiz option interface representing a single answer choice.
 * Provides rich metadata for each possible answer in a question.
 */
export interface QuizOption {
  /** Unique option identifier */
  id: string
  /** Display text */
  label: string
  /** Associated value for scoring/logic */
  value: string | number
  /** Optional image for visual questions */
  imageUrl?: string
  /** Detailed explanation of this option */
  explanation?: string
  /** Additional descriptive text */
  description?: string
  /** Hover tooltip content */
  tooltip?: string
}

/**
 * Quiz question interface extending QuestionInput.
 * Defines comprehensive structure for individual quiz questions including
 * answers, feedback, and associated learning resources.
 */
export interface QuizQuestion extends QuestionInput {
  /** Platform this question is for */
  platform?: Platform
  /** Available answer choices */
  options: QuizOption[]
  /** Question weight for scoring (default: 1) */
  weight?: number
  /** Topic/subject category */
  category?: string
  /** Question image URL for visual questions */
  imageUrl?: string
  /** Expected answer(s) */
  correctAnswer: string | string[]
  /** Answer explanations */
  explanation: {
    /** Explanation for correct response */
    correct: string
    /** Mapped explanations for each incorrect option */
    incorrect: Record<string, string> & { default?: string }
  }
  /** Show description for the question */
  showDescription?: boolean
  /** Question complexity level */
  difficulty?: DifficultyLevel
  /** Input placeholder text */
  placeholder?: string
  /** Unit for number questions (e.g., "kg", "m/s") */
  unit?: string
  /** Acceptable range for number questions */
  range?: {
    min: number
    max: number
  }
  /** Tolerance for number questions (acceptable +/- deviation from correct answer) */
  tolerance?: number
  /** 
   * Supplementary learning materials associated with this question.
   * Used to provide additional resources for learning and improvement.
   */
  learningContent?: {
    /** Title of the learning content section */
    title: string
    /** Description of what users will learn */
    description: string
    /** Array of related learning resources */
    resources: SupplementalResourceReference[]
  }
}

// =================================================
// Quiz Feedback Triggers & Recommendations
// =================================================

/**
 * Base interface for all score-based triggers.
 * Establishes the minimum (and optionally maximum) score levels and an optional scope.
 * If scope is unspecified, the trigger applies globally.
 */
export interface ScoreTrigger {
  /** Minimum score threshold that activates this trigger */
  minScore: number
  /** Optional maximum score cap for this trigger's activation range */
  maxScore?: number
  /** Optional scope to limit the trigger to a specific category or section */
  scope?: string
}

/**
 * Trigger for providing basic textual feedback.
 * Simple messages that are activated when a score meets or exceeds the threshold.
 *
 * @example
 * const basicTrigger: SimpleFeedbackTrigger = {
 *   minScore: 70,
 *   feedback: "Good work! You've demonstrated solid understanding."
 * }
 */
export interface SimpleFeedbackTrigger extends ScoreTrigger {
  /** Feedback message displayed when the trigger conditions are met */
  feedback: string
}

/**
 * Trigger for detailed feedback that includes insights on strengths and areas to improve.
 *
 * @example
 * const detailedTrigger: DetailedFeedbackTrigger = {
 *   minScore: 80,
 *   feedback: "Excellent performance!",
 *   strengths: ["Strong analytical skills", "Solid technical knowledge"],
 *   areasForImprovement: ["Consider deeper platform-specific optimizations"]
 * }
 */
export interface DetailedFeedbackTrigger extends ScoreTrigger {
  /** Primary feedback message */
  feedback: string
  /** Specific areas where the user excels */
  strengths?: string[]
  /** Specific areas for potential improvement */
  areasForImprovement?: string[]
}

/**
 * Defines the structure for a recommended resource to support further learning.
 *
 * @example
 * const recommendation: ResourceRecommendation = {
 *   title: "Advanced SEO Techniques",
 *   baseUrl: "https://example.com",
 *   slug: "/courses/advanced-seo",
 *   type: "course",
 *   difficulty: "intermediate",
 *   tags: ["seo", "technical"]
 * }
 * @deprecated Use SupplementalResourceReference from @base.ts instead
 */
export interface ResourceRecommendation {
  /** Optional text to provide additional context for the recommendation */
  text?: string
  /** Title of the recommended resource */
  title: string
  /** Base URL for remote resource location (leave empty for internal resources) */
  baseUrl?: string
  /** Resource path or identifier appended to baseUrl */
  slug: string
  /** Classification of the resource type */
  type: LearningResourceType
  /** Optional description providing more details about the resource */
  description?: string
  /** Optional tags to categorize and filter the resource */
  tags?: string[]
  /** Optional indicator of the resource's difficulty level */
  difficulty?: DifficultyLevel
  /** Boolean flag indicating if the resource is internal */
  isInternal?: boolean
}

/**
 * A recommendation item can either be a simple bullet point string or a detailed resource object.
 */
export type RecommendationItem = string | SupplementalResourceReference

/**
 * Represents a structured recommendation generated from quiz results.
 * Combines descriptive text with relevant learning resources and metadata
 * to provide actionable guidance based on quiz performance.
 * 
 * @interface QuizRecommendation
 * @property {string} text - Descriptive text explaining the recommendation
 * @property {SupplementalResourceReference[]} resources - Learning resources supporting the recommendation
 * @property {'high' | 'medium' | 'low'} priority - Importance/urgency level of the recommendation
 * @property {string} category - Classification for grouping related recommendations
 * 
 * @example
 * ```ts
 * const recommendation: QuizRecommendation = {
 *   text: "Focus on improving your analytics skills",
 *   resources: [{
 *     learningResourceType: 'course',
 *     isInternal: true,
 *     title: 'Analytics Fundamentals',
 *     urlSlug: '/courses/analytics-101'
 *   }],
 *   priority: 'high',
 *   category: 'analytics'
 * }
 * ```
 */
export interface QuizRecommendation {
  /** Text description of the recommendation */
  text: string
  /** Array of resource objects associated with this recommendation */
  resources: SupplementalResourceReference[]
  /** Priority level of the recommendation */
  priority: 'high' | 'medium' | 'low'
  /** Category to group related recommendations */
  category: string
}

/**
 * Trigger for providing resource recommendations based on quiz performance.
 * Can include both basic textual suggestions and detailed resource recommendation objects.
 *
 * @example
 * const resourceTrigger: ResourceTrigger = {
 *   minScore: 60,
 *   maxScore: 75,
 *   feedback: "Consider these resources to strengthen your knowledge:",
 *   recommendations: [
 *     "Review the basics of SEO",
 *     {
 *       title: "Advanced SEO Techniques",
 *       baseUrl: "https://example.com",
 *       slug: "/courses/advanced-seo",
 *       type: "course",
 *       difficulty: "intermediate",
 *       tags: ["seo", "technical"]
 *     }
 *   ]
 * }
 */
export interface ResourceTrigger extends ScoreTrigger {
  /** Optional contextual message introducing the recommendations */
  feedback?: string
  /** Array of recommendation items (simple text or detailed objects) */
  recommendations: RecommendationItem[]
}

/**
 * Comprehensive trigger that combines detailed feedback with resource recommendations.
 * Designed for an in-depth performance assessment, including targeted next steps.
 *
 * @example
 * const complexTrigger: ComplexTrigger = {
 *   minScore: 85,
 *   feedback: "Outstanding performance!",
 *   strengths: ["Advanced concept mastery"],
 *   recommendations: [
 *     "Consider applying these advanced techniques in your projects",
 *     {
 *       title: "Expert Level Strategies",
 *       baseUrl: "https://example.com",
 *       slug: "/articles/expert-strategies",
 *       type: "article",
 *       difficulty: "advanced"
 *     }
 *   ],
 *   nextSteps: ["Apply concepts to real projects"],
 *   priority: 1
 * }
 */
export interface ComplexTrigger extends DetailedFeedbackTrigger {
  /** Detailed recommendations that can include text bullet points and resource objects */
  recommendations?: RecommendationItem[]
  /** Specific actions suggested for further improvement */
  nextSteps?: string[]
  /** Priority level for the trigger (higher number signifies higher importance) */
  priority?: number
}

/**
 * Union type representing any quiz trigger.
 * Simplifies configuration by encapsulating all trigger types.
 */
export type QuizTrigger = SimpleFeedbackTrigger | DetailedFeedbackTrigger | ResourceTrigger | ComplexTrigger

// =================================================
// Quiz Metrics and Results Configuration
// =================================================

/**
 * Represents a metric for tracking and analyzing quiz performance.
 *
 * @example
 * const metric: QuizMetric = {
 *   id: "accuracy",
 *   label: "Accuracy",
 *   format: "percentage",
 *   thresholds: [
 *     { value: 50, label: "Needs Improvement", type: "min" },
 *     { value: 80, label: "Good", type: "max" }
 *   ]
 * }
 */
export interface QuizMetric {
  /** Unique identifier for the metric */
  id: string
  /** Human-readable name of the metric */
  label: string
  /** Optional format for displaying metric values */
  format?: MetricFormat
  /** Array of thresholds defining performance levels */
  thresholds?: Array<{
    value: number
    label: string
    type: ThresholdType
  }>
}

/**
 * Score-based feedback trigger with specific targeting.
 * Links feedback to specific sections, categories, or overall scores.
 */
export interface ScoredFeedbackTrigger extends ScoreTrigger {
  /** Unique identifier for this trigger */
  id: string
  /** Primary feedback message */
  feedback: string
  /** Specific areas where the user excels */
  strengths?: string[]
  /** Specific areas for potential improvement */
  areasForImprovement?: string[]
  /** Recommendations for improvement */
  recommendations?: RecommendationItem[]
  /** Next steps for the user */
  nextSteps?: string[]
  /** Target type this trigger applies to */
  targetType: 'section' | 'category' | 'overall' | 'platform'
  /** Target identifier (section ID, category name, or 'overall') */
  targetId: string
  /** Priority of this trigger (higher numbers take precedence) */
  priority?: number
}

/**
 * Comprehensive results configuration for quizzes.
 * Provides detailed feedback at multiple levels.
 */
export interface QuizResultsConfig {
  /** Metrics used for scoring */
  metrics: QuizMetric[]
  /** Overall score triggers */
  overallTriggers: ScoredFeedbackTrigger[]
  /** Section-specific triggers */
  sectionTriggers: Record<string, ScoredFeedbackTrigger[]>
  /** Category-specific triggers */
  categoryTriggers: Record<string, ScoredFeedbackTrigger[]>
  /** Platform-specific triggers */
  platformTriggers?: Record<string, ScoredFeedbackTrigger[]>
  /** Minimum scores required for different achievements */
  thresholds?: {
    /** Minimum passing score */
    pass?: number
    /** Score needed for different achievement levels */
    achievements?: {
      expert: number
      advanced: number
      intermediate: number
      beginner: number
    }
  }
}

/**
 * Detailed breakdown of quiz performance.
 */
export interface ScoreBreakdown {
  /** Raw score (number of correct answers) */
  score: number
  /** Total possible points */
  total: number
  /** Percentage score */
  percentage: number
  /** Triggered feedback for this score */
  feedback?: {
    /** Main feedback message */
    message: string
    /** Identified strengths */
    strengths?: string[]
    /** Areas needing improvement */
    areasForImprovement?: string[]
    /** Specific recommendations */
    recommendations?: RecommendationItem[]
    /** Suggested next steps */
    nextSteps?: string[]
  }
  /** Achievement level based on score */
  achievement?: 'expert' | 'advanced' | 'intermediate' | 'beginner'
}

/**
 * Comprehensive quiz results with detailed breakdowns.
 */
export interface QuizResult {
  /** Overall quiz score */
  score: number
  /** Total number of questions */
  totalQuestions: number
  /** Pass/fail status */
  passed: boolean
  /** Achievement level */
  achievement?: 'expert' | 'advanced' | 'intermediate' | 'beginner'
  
  /** Detailed section results */
  sectionResults: Record<string, ScoreBreakdown>
  /** Results by category */
  categoryResults?: Record<string, ScoreBreakdown>
  /** Platform-specific results */
  platformResults?: Record<Platform, ScoreBreakdown>
  
  /** Individual question responses */
  responses: Array<{
    questionId: string
    sectionId: string
    category?: string
    platform?: Platform
    correct: boolean
    answer: string | string[]
    timeTaken?: number
  }>
  
  /** Overall feedback summary */
  feedback: {
    /** Primary feedback message */
    summary: string
    /** Key strengths identified */
    strengths: string[]
    /** Areas needing improvement */
    areasForImprovement: string[]
    /** Specific recommendations */
    recommendations: RecommendationItem[]
    /** Suggested next steps */
    nextSteps: string[]
  }
}


// ==============================
// Configuration Interfaces
// ==============================

/**
 * Constants defining when feedback should be provided during a quiz experience.
 * Used to configure the timing and granularity of feedback presentation.
 */
export const FeedbackTiming = {
  /** Provide feedback immediately after each question */
  IMMEDIATE: 'immediate',
  /** Provide feedback at the end of each quiz section */
  SECTION: 'section', 
  /** Only provide feedback after the entire quiz is completed */
  END: 'end'
} as const

export type FeedbackTiming = typeof FeedbackTiming[keyof typeof FeedbackTiming]

/**
 * Configuration interface for quiz feedback settings.
 * Controls the visual and interactive aspects of feedback presentation.
 * 
 * @interface FeedbackSettings
 * @property {boolean} [showVisualIndicators] - Show immediate visual feedback (checkmarks/X's)
 * @property {boolean} [showDetailedExplanations] - Show text explanations for answers
 * @property {boolean} [enableHints] - Allow progressive hints to be revealed
 * @property {boolean} [allowAnswerReview] - Enable reviewing answers after completion
 * @property {boolean} [showSectionSummaries] - Display summaries after each section
 * 
 * Visual Feedback Options:
 * - showVisualIndicators: Immediately highlights correct/incorrect answers
 *   using visual cues like checkmarks or X's
 * - showDetailedExplanations: Provides in-depth explanations for why answers
 *   are correct or incorrect
 * 
 * Interactive Features:
 * - enableHints: Allows users to reveal progressive hints when stuck
 * - allowAnswerReview: Lets users review their answers and explanations
 *   after quiz completion
 * 
 * Summary Features:
 * - showSectionSummaries: Displays performance summaries and key takeaways
 *   after each quiz section
 */
export interface FeedbackSettings {
  /** Show immediate visual indicators for correct/incorrect answers */
  showVisualIndicators?: boolean
  /** Show detailed explanations for answer correctness */
  showDetailedExplanations?: boolean
  /** Enable progressive hints system */
  enableHints?: boolean
  /** Allow post-completion answer review */
  allowAnswerReview?: boolean
  /** Show section performance summaries */
  showSectionSummaries?: boolean
}

/**
 * Configuration interface for quiz feedback and explanations.
 * Controls when and how feedback is presented during the quiz experience.
 * 
 * @interface QuizFeedback
 * @property {FeedbackTiming} timing - When feedback should be displayed
 * @property {FeedbackSettings} settings - How feedback should be presented
 * 
 * The feedback system is split into two main components:
 * 
 * 1. Timing Configuration:
 * Controls when feedback is presented to users during the quiz experience.
 * Options include:
 * - immediate: After each question
 * - section: At the end of each section
 * - end: Only after quiz completion
 * 
 * 2. Settings Configuration:
 * Controls the presentation and interactive features of the feedback system.
 * Includes visual indicators, explanations, hints, and review capabilities.
 * 
 * This modular structure allows for flexible and precise control over the
 * entire feedback experience while maintaining clear separation of concerns.
 */
export interface QuizFeedback {
  /** When feedback should be displayed */
  timing: FeedbackTiming
  /** How feedback should be presented */
  settings: FeedbackSettings
}

/**
 * Complete quiz configuration options.
 */
export interface QuizConfig extends BaseToolConfig<QuizQuestion> {
  /** Quiz sections */
  sections?: QuizSection[]
  /** Quiz steps with weighted questions */
  steps?: Array<BaseStep & {
    questions?: Array<QuizQuestion & {
      weight?: number
      category?: string
    }>
  }>
  /** Assessment type */
  assesses?: string[]
  /** Teaches */
  teaches?: string[]
  /** Results configuration */
  results?: QuizResultsConfig
  /** Passing score percentage (0-100) */
  passingScore?: number
  /** Whether quiz can be retaken */
  allowRetake?: boolean
  /** Whether to show platform badges */
  showPlatformBadges?: boolean
  /** Custom recommendation rules */
  recommendationRules?: Array<{
    /** Rule condition */
    condition: string
    /** Recommendations can include both simple strings and detailed resource recommendations */
    recommendations: RecommendationItem[]
  }>
  /** Quiz feedback configuration */
  feedbackDefaults?: QuizFeedback
  /** UI preferences */
  ui?: {
    /** Show progress visualization */
    showProgress?: boolean
  }
}

/**
 * Quiz interface extending BaseTool.
 * Represents a complete quiz resource with sections and configuration.
 */
export interface Quiz extends BaseTool<QuizConfig> {
  /** Resource type */
  resourceType: 'tool'
  /** Tool type identifier */
  toolType: 'quiz'
  /** Quiz content sections */
  sections: QuizSection[]
  /** Quiz behavior configuration */
  config: QuizConfig
}

// `isQuiz` lives in `types/shared/type-guards.ts` (single canonical home;
// A5/D1 dedup). Import it from there.

/**
 * Resource type for learning materials and recommendations
 * @deprecated Use SupplementalResourceReference from @base.ts instead
 */
export interface QuizResource {
  /** Resource title */
  title: string
  /** Resource URL */
  url: string
  /** Resource type */
  type: LearningResourceType
  /** Optional description */
  description?: string
}

/**
 * Type guard to check if a value is a valid QuestionType
 */
export function isQuestionType(value: unknown): value is QuestionType {
  return typeof value === 'string' && Object.values(QuestionTypes).includes(value as QuestionType)
}

/**
 * Type guard to check if a value is a valid Platform
 */
export function isPlatform(value: unknown): value is Platform {
  return typeof value === 'string' && Object.values(Platforms).includes(value as Platform)
}

/**
 * Type guard to check if a value is a valid MetricFormat
 */
export function isMetricFormat(value: unknown): value is MetricFormat {
  return typeof value === 'string' && Object.values(MetricFormats).includes(value as MetricFormat)
}

/**
 * Type guard to check if a value is a valid ThresholdType
 */
export function isThresholdType(value: unknown): value is ThresholdType {
  return typeof value === 'string' && Object.values(ThresholdTypes).includes(value as ThresholdType)
}

/**
 * Validation result for quiz answers
 */
export interface ValidationResult {
  isValid: boolean
  isCorrect: boolean
  error?: string
  details?: {
    message: string
    code: ValidationErrorCode
  }
}

/**
 * Validation error codes for quiz answers
 */
export const ValidationErrorCodes = {
  INVALID_FORMAT: 'invalid_format',
  MISSING_ANSWER: 'missing_answer',
  INVALID_RANGE: 'invalid_range',
  INCOMPLETE_ANSWER: 'incomplete_answer',
  INVALID_CONFIG: 'invalid_config'
} as const

export type ValidationErrorCode = typeof ValidationErrorCodes[keyof typeof ValidationErrorCodes]

/**
 * Type guard to check if a value is a valid ValidationErrorCode
 */
export function isValidationErrorCode(value: unknown): value is ValidationErrorCode {
  return typeof value === 'string' && Object.values(ValidationErrorCodes).includes(value as ValidationErrorCode)
}

